import { AppliedFilters, tailwindOverride } from '@getsynapse/design-system';
import { PropsWithChildren, useCallback, useMemo } from 'react';
import { DATE } from 'utils/constants';
import { AppliedFilter, Option } from 'utils/customTypes';
import {
  getDateFilterLabel,
  getFilterOptionLabelByKeyAndValue,
} from 'utils/filters';
import {
  isDateRangeFilter,
  isFilterValueIsOfStringType,
  isBooleanFilter,
} from 'utils/typeGuards';
import {
  EntityFilteredType,
  StandardAndQuickEntityFilteredType,
  Filter,
} from 'utils/types/filters';
import intl from 'react-intl-universal';
type FilterType<T> =
  | EntityFilteredType<T>
  | StandardAndQuickEntityFilteredType<T>;

interface AppliedFiltersTagsProps<T> {
  savedFilters: FilterType<T>;
  onUpdateFilters: (filters: FilterType<T> | {}) => void;
  onDisplayAllFilters: () => void;
  getOptionsbyKey: (key: keyof T) => Option[];
  dataTestid: string;
}

const AppliedFiltersTags = <T,>({
  savedFilters,
  onUpdateFilters,
  onDisplayAllFilters,
  getOptionsbyKey,
  dataTestid,
}: PropsWithChildren<AppliedFiltersTagsProps<T>>) => {
  const appliedFilters = useMemo<AppliedFilter[]>(() => {
    const tags: AppliedFilter[] = [];

    for (const key of Object.keys(savedFilters)) {
      const filterKey = key as keyof T;
      const filterValue: Filter = savedFilters[filterKey];
      if (!isFilterValueIsOfStringType(filterValue)) {
        if (isBooleanFilter(filterValue)) {
          // Only show the tag if the filter is true (when archived programs are included)
          if (filterValue) {
            const filterKeyUpper = filterKey.toString().toUpperCase();
            const labelKey = `PROGRAMS_LIST_PAGE.APPLIED_FILTERS.${filterKeyUpper}`;
            const fallback = intl
              .get('PROGRAMS_LIST_PAGE.STATUS.ARCHIVED')
              .defaultMessage('Archived');

            const label = intl.get(labelKey).defaultMessage(fallback);
            tags.push({ label, value: filterValue.toString(), key });
          }
          // Don't show a tag when archived filter is false (normal state)
        } else if (isDateRangeFilter(filterValue)) {
          const label = getDateFilterLabel(filterValue, DATE.TASK_TABLE_FORMAT);
          tags.push({ label, value: '', key });
        } else {
          const filterOptions = getOptionsbyKey(filterKey);
          filterValue.length &&
            filterValue.forEach((value: string) => {
              const label = getFilterOptionLabelByKeyAndValue(
                filterOptions,
                value
              );
              tags.push({ label, value, key });
            });
        }
      }
    }

    return tags;
  }, [getOptionsbyKey, savedFilters]);
  const removeFilter = useCallback(
    (filter: AppliedFilter) => {
      const filterKey = filter.key as keyof T;

      // Special handling for budgetId removal
      if (filter.key === 'budgetId' && 'budgetId' in savedFilters) {
        const newSavedFilters = Object.fromEntries(
          Object.entries(savedFilters).filter(([key]) => key !== 'budgetId')
        );

        const validFilterCount = Object.keys(newSavedFilters).length;
        if (validFilterCount === 1) {
          onUpdateFilters({});
          return;
        }
        onUpdateFilters(newSavedFilters);
        return;
      }

      const updatedFilters = { ...savedFilters };
      const filterValue: Filter = updatedFilters[filterKey];

      if (isBooleanFilter(filterValue) || isDateRangeFilter(filterValue)) {
        delete updatedFilters[filterKey];
      } else {
        const updatedFilterValues = filterValue.filter(
          (value) => value !== filter.value
        );
        if (updatedFilterValues.length === 0) {
          delete updatedFilters[filterKey];
        } else {
          updatedFilters[filterKey] =
            updatedFilterValues as EntityFilteredType<T>[keyof T];
        }
      }

      // Keep the budget quick-filter hidden when it's the only remaining entry.
      if (
        Object.keys(updatedFilters).length === 1 &&
        'budgetId' in updatedFilters
      ) {
        onUpdateFilters({});
        return;
      }
      onUpdateFilters(updatedFilters);
    },
    [onUpdateFilters, savedFilters]
  );

  return (
    <AppliedFilters<AppliedFilter>
      filters={appliedFilters}
      onRemoveFilter={removeFilter}
      onClearAll={() => onUpdateFilters({})}
      onDisplayAllFilters={onDisplayAllFilters}
      className={tailwindOverride('border-primary-lighter-two z-20')}
      data-testid={dataTestid}
      title={intl.get('FILTER_PANEL.FILTERS_APPLIED')}
      clearAllLabel={intl.get('FILTER_PANEL.CLEAR_ALL')}
    />
  );
};

export default AppliedFiltersTags;
